/**
 * FilterRow — search-UX §3.5 / §3.7 / §3.8 / §3.9 — reusable
 * "search input + chip row" pattern.
 *
 * Used by:
 *  - BrowseScreen Crew layout (filter your circles)
 *  - ShareFlowScreen (filter shares by counterparty + source)
 *  - NotificationsScreen (search + type/read chips)
 *  - ScanHistoryScreen (filter by status + date)
 *
 * Notion's per-database search + Linear's saved-view chip row are
 * the canonical patterns: chips for the 3-5 most useful slicing
 * axes, a text input layered on top for the long tail.  All four
 * surfaces had ZERO search/filter chrome before this — adding the
 * same component keeps the visual + interaction consistent.
 *
 * Pure presentation — caller owns the query + chip selection state.
 * Chip set is variable so each surface can pass its own taxonomy
 * (Notifications types vs Scan statuses vs Share sources).
 */

import React from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../styles/theme';

export interface FilterChip {
  /** Display label for the chip (e.g. "All", "Unread", "Borrow"). */
  label: string;
  /** Stable key used by `activeKey` to determine the active chip. */
  key: string;
}

export interface FilterRowProps {
  /** Current free-text query. */
  query: string;
  /** Fires on every keystroke. */
  onQueryChange: (next: string) => void;
  /** Placeholder for the text input.  Spell out the scope —
   *  "Search notifications" / "Search items in {GroupName}". */
  placeholder: string;
  /** Optional chip set.  Pass null/empty to skip rendering the
   *  chip row entirely (text-only filter). */
  chips?: FilterChip[];
  /** The currently active chip key.  When null, the first chip's
   *  pressed state acts as "show all". */
  activeKey?: string | null;
  /** Fires when the user taps a chip. */
  onChipPress?: (key: string) => void;
  /** Optional second chip row — for surfaces that filter by TWO
   *  taxonomies (ScanHistory: status × date). */
  chipsSecondary?: FilterChip[];
  activeSecondaryKey?: string | null;
  onSecondaryChipPress?: (key: string) => void;
  /** Optional testID prefix; the text input gets `${testID}-input`
   *  and the clear button `${testID}-clear`. */
  testID?: string;
}

export const FilterRow: React.FC<FilterRowProps> = ({
  query,
  onQueryChange,
  placeholder,
  chips,
  activeKey,
  onChipPress,
  chipsSecondary,
  activeSecondaryKey,
  onSecondaryChipPress,
  testID,
}) => {
  return (
    <View style={styles.wrap}>
      <View style={styles.inputRow}>
        <MaterialIcons
          name="search"
          size={16}
          color={fpColors.outline}
          style={{ marginRight: 6 }}
        />
        <TextInput
          value={query}
          onChangeText={onQueryChange}
          placeholder={placeholder}
          placeholderTextColor={fpColors.outline}
          style={styles.input}
          autoCorrect={false}
          autoCapitalize="none"
          accessibilityLabel={placeholder}
          testID={testID ? `${testID}-input` : undefined}
        />
        {query.length > 0 && (
          <Pressable
            onPress={() => onQueryChange('')}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            testID={testID ? `${testID}-clear` : undefined}
          >
            <MaterialIcons name="close" size={16} color={fpColors.outline} />
          </Pressable>
        )}
      </View>

      {chips && chips.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.chipRow}
        >
          {chips.map((c) => {
            const active = (activeKey ?? chips[0].key) === c.key;
            return (
              <Pressable
                key={`chip-${c.key}`}
                onPress={() => onChipPress?.(c.key)}
                style={({ pressed }) => [
                  styles.chip,
                  active && styles.chipActive,
                  pressed && { opacity: 0.7 },
                ]}
                accessibilityRole="button"
                accessibilityLabel={c.label}
                testID={testID ? `${testID}-chip-${c.key}` : undefined}
              >
                <Text style={[styles.chipLabel, active && styles.chipLabelActive]}>
                  {c.label}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
      )}

      {chipsSecondary && chipsSecondary.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[styles.chipRow, { marginTop: 4 }]}
        >
          {chipsSecondary.map((c) => {
            const active =
              (activeSecondaryKey ?? chipsSecondary[0].key) === c.key;
            return (
              <Pressable
                key={`chip2-${c.key}`}
                onPress={() => onSecondaryChipPress?.(c.key)}
                style={({ pressed }) => [
                  styles.chip,
                  active && styles.chipActive,
                  pressed && { opacity: 0.7 },
                ]}
                accessibilityRole="button"
                accessibilityLabel={c.label}
                testID={
                  testID ? `${testID}-chip2-${c.key}` : undefined
                }
              >
                <Text style={[styles.chipLabel, active && styles.chipLabelActive]}>
                  {c.label}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 6,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginBottom: 8,
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
    padding: 0,
    margin: 0,
  },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  chipActive: {
    borderColor: fpColors.primary,
    backgroundColor: fpColors.primary,
  },
  chipLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.onSurface,
  },
  chipLabelActive: {
    color: fpColors.onPrimary,
  },
});

export default FilterRow;
